import React, { useState } from 'react';
import { Iuser, Ifacility, IWorkOrder, WorkOrderSource } from '../../models';
import { useDispatch, useSelector } from 'react-redux';
import { TFunction } from 'i18next';
import { getIsMobile } from '../../reducers/configReducer';
import ReactTable from 'react-table';
import { toastr } from 'react-redux-toastr';
import { constants } from '../../constants/constants';
import { addTestSapWorkOrder } from '../../actions/manageWorkOrderActions';
import { Button } from 'react-bootstrap';
import { initialWorkOrder } from '../../reducers/initialState';
import { setSelectedInstallBaseID } from '../../actions/manageInventoryActions';

interface Iprops {
    user: Iuser;
    selectedFacility: Ifacility;
    t: TFunction;
    tableData: any;
    selectedInstallBaseID: string;
}

const ManageTestingInventory: React.FC<Iprops> = (props: Iprops) => {
    const { tableData, selectedInstallBaseID } = props;

    const isMobile = useSelector(getIsMobile);
    const dispatch = useDispatch();
    const [formValues, setFormValues] = useState({
        sapPlant: '',
        sapBusinessIndicator: '',
        sapAccountIndicator: '',
        workOrderNumber: ''
    });

    const scrollingHeight = isMobile ? 'calc(100dvh - 60px - 84px)' : '100%';
    const overFlow = isMobile ? 'auto' : 'initial';

    const columns = () => [
        {
            Header: 'Name',
            accessor: 'name'
        },
        {
            id: 'isVirtual',
            Header: 'Is Virtual',
            accessor: (row: any) => {
                const product = row.installBases?.[0]?.product;
                return product?.subcategoryID ===
                    constants.virtualProductSubcategoryID
                    ? 'Yes'
                    : 'No';
            }
        }
    ];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        setFormValues(prev => ({
            ...prev,
            [id.replace('testing-', '')]: value
        }));
    };

    const isFormValid = () => {
        return (
            selectedInstallBaseID &&
            formValues.sapPlant.trim() !== '' &&
            formValues.sapBusinessIndicator.trim() !== '' &&
            formValues.sapAccountIndicator.trim() !== '' &&
            formValues.workOrderNumber.trim() !== ''
        );
    };

    const handleClear = () => {
        dispatch(setSelectedInstallBaseID(''));
        setFormValues({
            sapPlant: '',
            sapBusinessIndicator: '',
            sapAccountIndicator: '',
            workOrderNumber: ''
        });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);

        const values = {
            installBaseID: selectedInstallBaseID,
            sapBusinessIndicator: formData.get(
                'sapBusinessIndicator'
            ) as string,
            sapPlant: formData.get('sapPlant') as string,
            sapAccountIndicator: formData.get('sapAccountIndicator') as string,
            workOrderNumber: formData.get('workOrderNumber') as string
        };

        try {
            if (!values.installBaseID) {
                toastr.error(
                    'Error',
                    'Install Base ID is required',
                    constants.toastrError
                );
                return;
            }

            if (!values.sapBusinessIndicator) {
                toastr.error(
                    'Error',
                    'SAP Business Indicator is required',
                    constants.toastrError
                );
                return;
            }

            if (!values.sapPlant) {
                toastr.error(
                    'Error',
                    'SAP Plant is required',
                    constants.toastrError
                );
                return;
            }

            if (!values.sapAccountIndicator) {
                toastr.error(
                    'Error',
                    'SAP Account Indicator is required',
                    constants.toastrError
                );
                return;
            }

            if (!values.workOrderNumber) {
                toastr.error(
                    'Error',
                    'Work Order Number is required',
                    constants.toastrError
                );
            }

            // Create new work order object with SAP source
            const newWorkOrder: IWorkOrder & { workOrderNumber: string } = {
                ...initialWorkOrder,
                id: '',
                source: WorkOrderSource.SAP,
                installBaseID: values.installBaseID,
                sapBusinessIndicator: values.sapBusinessIndicator,
                sapPlant: values.sapPlant,
                sapAccountIndicator: values.sapAccountIndicator,
                workOrderNumber: values.workOrderNumber
            };

            dispatch(addTestSapWorkOrder(newWorkOrder));
            handleClear();
        } catch (error) {
            constants.handleError(
                error,
                'Failed to create test SAP work order'
            );
        }
    };

    const noSelectionText = 'No Install Base / Asset Selected';

    const selectedInstallBase = selectedInstallBaseID
        ? tableData.find(
              (item: any) =>
                  item.installBases?.[0]?.id === selectedInstallBaseID
          )
        : null;

    const installBaseName = selectedInstallBase?.name ?? noSelectionText;

    return (
        <div>
            <div className="testing-inventory-form-container">
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '15px'
                    }}
                >
                    <div>
                        <h4 style={{ margin: 0 }}>{installBaseName}</h4>
                        <p>Install Base ID: {selectedInstallBaseID ?? '—'}</p>
                    </div>
                    {selectedInstallBaseID && (
                        <Button
                            bsStyle="default"
                            bsSize="small"
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    )}
                </div>
                <form
                    className="testing-inventory-form"
                    onSubmit={handleSubmit}
                >
                    <div className="testing-inventory-form-row">
                        <label htmlFor="testing-sapPlant">SAP Plant</label>
                        <input
                            type="text"
                            id="testing-sapPlant"
                            name="sapPlant"
                            value={formValues.sapPlant}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="testing-inventory-form-row">
                        <label htmlFor="testing-sapBusinessIndicator">
                            SAP Business Indicator
                        </label>
                        <input
                            type="text"
                            id="testing-sapBusinessIndicator"
                            name="sapBusinessIndicator"
                            value={formValues.sapBusinessIndicator}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="testing-inventory-form-row">
                        <label htmlFor="testing-sapAccountIndicator">
                            SAP Account Indicator
                        </label>
                        <input
                            type="text"
                            id="testing-sapAccountIndicator"
                            name="sapAccountIndicator"
                            value={formValues.sapAccountIndicator}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="testing-inventory-form-row">
                        <label htmlFor="testing-workOrderNumber">
                            Work Order #
                        </label>
                        <input
                            type="text"
                            id="testing-workOrderNumber"
                            name="workOrderNumber"
                            value={formValues.workOrderNumber}
                            onChange={handleInputChange}
                        />
                    </div>
                    <Button
                        type="submit"
                        bsStyle="primary"
                        className="testing-inventory-form-button"
                        disabled={!isFormValid()}
                    >
                        Create WO
                    </Button>
                </form>
            </div>

            <div style={{ height: scrollingHeight, overflowY: overFlow }}>
                <ReactTable
                    data={tableData}
                    columns={columns()}
                    defaultPageSize={constants.tablePageSizeDefault}
                    pageSizeOptions={constants.tablePageSizeOptions}
                    getTrProps={(state: any, rowInfo: any) => {
                        return {
                            onClick: () => {
                                if (!rowInfo.original.id) {
                                    toastr.error(
                                        'Error',
                                        'Install Base ID is required',
                                        constants.toastrError
                                    );
                                    return;
                                }

                                const installBase = rowInfo.original
                                    .installBases
                                    ? rowInfo.original.installBases[0]
                                    : null;

                                if (!installBase) {
                                    toastr.error(
                                        'Error',
                                        'Install Base ID not found in data',
                                        constants.toastrError
                                    );
                                    return;
                                }

                                dispatch(
                                    setSelectedInstallBaseID(installBase?.id)
                                );
                            }
                        };
                    }}
                />
            </div>
        </div>
    );
};

export default ManageTestingInventory;
